import { OTHER_OPERATOR_SLUG, operatorBrand } from '@ev/shared'
import { useStationIndex } from '../../../data/hooks'
import { useStrings } from '../../../i18n'
import { untrackedOperatorLabel } from '../../../search/untracked'
import { hasPublishedResults, operatorNameMatchCount } from '../../../state/results'
import { selectFilters, selectResults, useAppStore } from '../../../state/store'

/**
 * The networks picked out of "Inni operatorzy", as rows under the brand list.
 *
 * Nothing is rendered until one is picked. There are several hundred of them and
 * listing them all would be the scrollable wall the registry's top-fifty cut
 * exists to avoid - they are reached through the search box, which is where
 * somebody who knows the name of one already is. What this adds is the other
 * half of that: once picked, a network has a row that shows it is on, carries
 * its count like every brand above it, and can be switched off without
 * remembering what was typed to switch it on.
 *
 * A row can outlive the artifact that named it - a filter arriving from a shared
 * link before `stations.geojson` lands, or a network that has since left the
 * data. It then shows the folded name it was stored as, which is still something
 * a reader can recognise and untick, rather than disappearing and leaving the
 * map narrowed by something invisible.
 */

const OTHER_OPERATOR_COLOR = operatorBrand(OTHER_OPERATOR_SLUG).color

/**
 * Nothing picked is the common case, and it costs nothing: the artifact is only
 * subscribed to from the inner component, so a panel with no untracked network
 * selected does not hold the national index open to render an empty list.
 */
export const UntrackedOperators = () => {
  const keys = useAppStore(selectFilters).operatorNames
  return keys.length === 0 ? null : <UntrackedOperatorRows keys={keys} />
}

const UntrackedOperatorRows = ({ keys }: { readonly keys: readonly string[] }) => {
  const strings = useStrings()
  const results = useAppStore(selectResults)
  const toggleFilter = useAppStore((state) => state.toggleFilter)

  const { resource } = useStationIndex()
  const untracked = resource.status === 'ready' ? resource.value.untracked : []
  const published = hasPublishedResults(results)

  return (
    <ul className="facet__list facet__list--untracked">
      {keys.map((key) => {
        const count = operatorNameMatchCount(results, key)
        return (
          <li key={key} className="facet__row">
            <label className="facet__option">
              <input
                className="facet__checkbox"
                type="checkbox"
                checked
                onChange={() => toggleFilter('operatorNames', key)}
              />

              <span
                className="facet__swatch"
                style={{ backgroundColor: OTHER_OPERATOR_COLOR }}
                aria-hidden="true"
              />

              <span className="facet__label">{untrackedOperatorLabel(untracked, key)}</span>

              {!published ? null : (
                <span className="facet__count">
                  <span className="visually-hidden">{strings.filters.matchingSites(count)}</span>
                  <span aria-hidden="true">{count}</span>
                </span>
              )}
            </label>
          </li>
        )
      })}
    </ul>
  )
}
